// src/pages/admin/analytics/components/RecipeCalculatorTab.jsx
import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Calculator,
  Plus,
  Trash2,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Calendar,
  Building,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Sparkles,
  Layers,
  ArrowRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import productService from '../../../../services/productService'
import recipesService from '../../../../services/recipesService'
import rawMaterialsService from '../../../../services/rawMaterialsService'
import reportsService from '../../../../services/reportsService'

// Helper function to safely format unit object or string into display string
const getUnitName = (unit) => {
  if (!unit) return 'unit'
  if (typeof unit === 'string') return unit
  if (typeof unit === 'object') {
    return unit.abbreviation || unit.name || unit.symbol || 'unit'
  }
  return String(unit)
}

export const RecipeCalculatorTab = ({ selectedBranch, startDate, endDate }) => {
  // Master lists
  const [products, setProducts] = useState([])
  const [recipes, setRecipes] = useState([])
  const [rawMaterials, setRawMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [salesLoading, setSalesLoading] = useState(false)

  // Selection states
  const [productTypeFilter, setProductTypeFilter] = useState('all') // 'all' | 'recipe' | 'product'
  const [selectedProductId, setSelectedProductId] = useState('')
  const [calcStartDate, setCalcStartDate] = useState(startDate || '')
  const [calcEndDate, setCalcEndDate] = useState(endDate || '')

  // Active calculator state
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [sellingPrice, setSellingPrice] = useState(0)
  const [soldQuantity, setSoldQuantity] = useState(1)
  const [directProductCost, setDirectProductCost] = useState(0)
  const [recipeIngredients, setRecipeIngredients] = useState([]) // Array of { id, rawMaterialId, name, unit, quantity, unitCost }

  // Custom Ingredient Add state
  const [selectedAddRmId, setSelectedAddRmId] = useState('')

  // 1. Initial Load of Master Products, Recipes & Raw Materials
  useEffect(() => {
    const fetchMasterData = async () => {
      setLoading(true)
      try {
        const [prodRes, recRes, rmRes] = await Promise.all([
          productService.getAllProducts({ limit: 500 }),
          recipesService.getAllRecipes({ limit: 500 }),
          rawMaterialsService.getAllRawMaterials({ limit: 500 })
        ])

        const prodList = prodRes?.data?.products || prodRes?.products || prodRes?.data || []
        const recList = recRes?.data?.recipes || recRes?.recipes || recRes?.data || []
        const rmList = rmRes?.data?.rawMaterials || rmRes?.rawMaterials || rmRes?.data || []

        setProducts(Array.isArray(prodList) ? prodList : [])
        setRecipes(Array.isArray(recList) ? recList : [])
        setRawMaterials(Array.isArray(rmList) ? rmList : [])

        // Default select first product
        if (Array.isArray(prodList) && prodList.length > 0) {
          setSelectedProductId(prodList[0]._id || prodList[0].id)
        }
      } catch (err) {
        console.error('Failed to load calculator master data:', err)
        toast.error('Failed to load products or recipes for calculator')
      } finally {
        setLoading(false)
      }
    }

    fetchMasterData()
  }, [])

  // Sync date props if changed from parent header
  useEffect(() => {
    if (startDate) setCalcStartDate(startDate)
    if (endDate) setCalcEndDate(endDate)
  }, [startDate, endDate])

  // Filtered Products List
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (productTypeFilter === 'recipe') return p.type === 'RECIPE_BASED' || p.type === 'recipe_based'
      if (productTypeFilter === 'product') return p.type === 'PRODUCT' || p.type === 'product'
      return true
    })
  }, [products, productTypeFilter])

  // Helper to load recipe data for a product
  const loadProductIntoCalculator = useCallback((prod) => {
    if (!prod) return

    setSelectedProduct(prod)
    setSellingPrice(prod.sellingPrice || prod.price || 0)
    setDirectProductCost(prod.cost || 0)

    const isRecipeBased = prod.type === 'RECIPE_BASED' || prod.type === 'recipe_based'

    if (isRecipeBased) {
      // Find matching recipe
      let recipe = null
      if (prod.recipe && typeof prod.recipe === 'object') {
        recipe = prod.recipe
      } else if (prod.recipe) {
        recipe = recipes.find((r) => String(r._id || r.id) === String(prod.recipe))
      }

      if (!recipe && prod._id) {
        recipe = recipes.find((r) => String(r.product?._id || r.product) === String(prod._id))
      }

      if (recipe && Array.isArray(recipe.rawMaterialList)) {
        const ingredients = recipe.rawMaterialList.map((item, idx) => {
          const rmObj = typeof item.rawMaterial === 'object' ? item.rawMaterial : rawMaterials.find((rm) => String(rm._id || rm.id) === String(item.rawMaterial))
          const unitCost = rmObj?.cost || rmObj?.purchasingPrice || (rmObj?.unitCost ? rmObj.unitCost : 0)

          return {
            id: `ing-${idx}-${Date.now()}`,
            rawMaterialId: rmObj?._id || item.rawMaterial,
            name: rmObj?.name || 'Raw Material',
            unit: getUnitName(rmObj?.unit || item.unit),
            quantity: item.quantity || 0,
            unitCost: Number(unitCost) || 0
          }
        })
        setRecipeIngredients(ingredients)
      } else {
        setRecipeIngredients([])
      }
    } else {
      setRecipeIngredients([])
    }
  }, [recipes, rawMaterials])

  // On Selected Product Change
  useEffect(() => {
    if (!selectedProductId || products.length === 0) return
    const prod = products.find((p) => String(p._id || p.id) === String(selectedProductId))
    if (prod) {
      loadProductIntoCalculator(prod)
    }
  }, [selectedProductId, products, loadProductIntoCalculator])

  // Fetch actual sales quantity for the date range & branch
  const fetchSalesQuantity = useCallback(async () => {
    if (!selectedProductId) return

    setSalesLoading(true)
    try {
      const filters = {}
      if (selectedBranch) filters.branch = selectedBranch
      if (calcStartDate) filters.startDate = calcStartDate
      if (calcEndDate) filters.endDate = calcEndDate

      const reportRes = await reportsService.generateProductWiseSalesReport(filters)
      const rows = reportRes?.data || []

      const targetProdRow = rows.find((r) => String(r.productId || r._id) === String(selectedProductId))
      if (targetProdRow && targetProdRow.quantitySold !== undefined) {
        setSoldQuantity(Math.max(1, targetProdRow.quantitySold))
        toast.success(`Found ${targetProdRow.quantitySold} units sold in date range!`, { id: 'sales-count' })
      } else {
        setSoldQuantity(1)
      }
    } catch (err) {
      console.warn('Could not fetch sales quantity for product:', err)
    } finally {
      setSalesLoading(false)
    }
  }, [selectedProductId, selectedBranch, calcStartDate, calcEndDate])

  useEffect(() => {
    if (selectedProductId) {
      fetchSalesQuantity()
    }
  }, [selectedProductId, selectedBranch, calcStartDate, calcEndDate, fetchSalesQuantity])

  // Ingredient Handlers
  const handleUpdateIngredientQty = (id, newQty) => {
    setRecipeIngredients((prev) =>
      prev.map((ing) => (ing.id === id ? { ...ing, quantity: Math.max(0, Number(newQty) || 0) } : ing))
    )
  }

  const handleUpdateIngredientCost = (id, newCost) => {
    setRecipeIngredients((prev) =>
      prev.map((ing) => (ing.id === id ? { ...ing, unitCost: Math.max(0, Number(newCost) || 0) } : ing))
    )
  }

  const handleRemoveIngredient = (id) => {
    setRecipeIngredients((prev) => prev.filter((ing) => ing.id !== id))
    toast.success('Ingredient removed from simulation', { icon: '🗑️' })
  }

  const handleAddIngredient = () => {
    if (!selectedAddRmId) {
      toast.error('Please select a raw material to add')
      return
    }

    const rmObj = rawMaterials.find((rm) => String(rm._id || rm.id) === String(selectedAddRmId))
    if (!rmObj) return

    const newIng = {
      id: `ing-custom-${Date.now()}`,
      rawMaterialId: rmObj._id || rmObj.id,
      name: rmObj.name,
      unit: getUnitName(rmObj.unit),
      quantity: 10,
      unitCost: Number(rmObj.cost || rmObj.purchasingPrice || 0)
    }

    setRecipeIngredients((prev) => [...prev, newIng])
    setSelectedAddRmId('')
    toast.success(`Added ${rmObj.name} to simulation!`)
  }

  const handleResetRecipe = () => {
    if (selectedProduct) {
      loadProductIntoCalculator(selectedProduct)
      toast.success('Recipe reset to database defaults')
    }
  }

  // Live Calculations
  const isRecipe = selectedProduct?.type === 'RECIPE_BASED' || selectedProduct?.type === 'recipe_based'

  const recipeCOGSPerUnit = useMemo(() => {
    if (isRecipe) {
      return recipeIngredients.reduce((sum, ing) => sum + ing.quantity * ing.unitCost, 0)
    }
    return Number(directProductCost) || 0
  }, [isRecipe, recipeIngredients, directProductCost])

  const marginPerUnit = useMemo(() => {
    return Number(sellingPrice) - recipeCOGSPerUnit
  }, [sellingPrice, recipeCOGSPerUnit])

  const marginPercent = useMemo(() => {
    const sp = Number(sellingPrice)
    if (sp <= 0) return 0
    return (marginPerUnit / sp) * 100
  }, [sellingPrice, marginPerUnit])

  const totalRevenue = useMemo(() => {
    return Math.max(0, Number(soldQuantity) || 0) * Number(sellingPrice)
  }, [soldQuantity, sellingPrice])

  const totalCost = useMemo(() => {
    return Math.max(0, Number(soldQuantity) || 0) * recipeCOGSPerUnit
  }, [soldQuantity, recipeCOGSPerUnit])

  const netProfitLoss = useMemo(() => {
    return totalRevenue - totalCost
  }, [totalRevenue, totalCost])

  const isProfitable = netProfitLoss >= 0

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-sm border border-gray-100 min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-500 font-medium">Loading products & recipes for calculator...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-amber-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 opacity-10 pointer-events-none">
          <Calculator className="w-64 h-64 text-white" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-wider mb-2">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Interactive Profit & Loss Simulator</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">Recipe & Product Calculator</h2>
            <p className="text-orange-100 text-sm mt-1 max-w-2xl">
              Simulate menu selling prices, recipe ingredients, and buying costs to instantly predict profit margins and detect unprofitable menu items before selling.
            </p>
          </div>

          <button
            onClick={fetchSalesQuantity}
            disabled={salesLoading}
            className="self-start md:self-center flex items-center gap-2 px-4 py-2.5 bg-white text-orange-600 hover:bg-orange-50 rounded-xl font-bold text-sm shadow-md transition-all disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${salesLoading ? 'animate-spin' : ''}`} />
            <span>Sync Sales Count</span>
          </button>
        </div>
      </div>

      {/* Control Filters Bar */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Product Category Filter */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              1. Product Type
            </label>
            <select
              value={productTypeFilter}
              onChange={(e) => setProductTypeFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-xl bg-white text-sm font-semibold text-gray-800 focus:ring-2 focus:ring-orange-500 focus:outline-none shadow-sm"
            >
              <option value="all">All Products ({products.length})</option>
              <option value="recipe">Recipe-Based Products Only</option>
              <option value="product">Direct Products Only</option>
            </select>
          </div>

          {/* Product Select */}
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              2. Select Product / Recipe
            </label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="w-full h-10 px-3 border border-orange-300 rounded-xl bg-orange-50/50 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none shadow-sm"
            >
              {filteredProducts.map((p) => (
                <option key={p._id || p.id} value={p._id || p.id}>
                  {p.name} — [{p.type === 'RECIPE_BASED' || p.type === 'recipe_based' ? 'Recipe-Based' : 'Direct Product'}] (Selling: Rs.{p.sellingPrice || p.price || 0})
                </option>
              ))}
            </select>
          </div>

          {/* Sold Quantity Input */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center justify-between">
              <span>3. Sold / Projected Qty</span>
              {salesLoading && <span className="text-[10px] text-orange-500 font-normal animate-pulse">Syncing...</span>}
            </label>
            <input
              type="number"
              min="1"
              value={soldQuantity}
              onChange={(e) => setSoldQuantity(Math.max(1, Number(e.target.value) || 1))}
              className="w-full h-10 px-3 border border-gray-300 rounded-xl text-sm font-bold text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none shadow-sm"
              placeholder="e.g. 100"
            />
          </div>
        </div>

        {/* Date Range & Price simulation row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              Date From
            </label>
            <input
              type="date"
              value={calcStartDate}
              onChange={(e) => setCalcStartDate(e.target.value)}
              className="w-full h-9 px-3 border border-gray-300 rounded-lg text-xs font-medium text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              Date To
            </label>
            <input
              type="date"
              value={calcEndDate}
              onChange={(e) => setCalcEndDate(e.target.value)}
              className="w-full h-9 px-3 border border-gray-300 rounded-lg text-xs font-medium text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-orange-700 mb-1 flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5 text-orange-500" />
              Simulated Selling Price (Rs.)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(Math.max(0, Number(e.target.value) || 0))}
              className="w-full h-9 px-3 border border-orange-400 rounded-lg text-sm font-extrabold text-orange-900 bg-orange-50/30 focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Main Grid: Left Side Recipe Editor, Right Side Financial Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Interactive Recipe / Product Simulator */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {isRecipe ? 'Recipe Ingredients Cost Simulator' : 'Direct Product Cost Simulator'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {isRecipe ? 'Tweak ingredient quantities or raw material unit prices to see live cost impact' : 'Simulate direct buying cost'}
                  </p>
                </div>
              </div>

              {isRecipe && (
                <button
                  onClick={handleResetRecipe}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Reset DB Recipe</span>
                </button>
              )}
            </div>

            {/* Recipe Ingredients Table */}
            {isRecipe ? (
              <div className="space-y-4">
                {recipeIngredients.length === 0 ? (
                  <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-gray-700">No Recipe Linked to this Product</p>
                    <p className="text-xs text-gray-500 mt-1">Add custom raw materials below to construct a test recipe!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-700 uppercase font-bold border-b border-gray-200">
                          <th className="py-2.5 px-3">Raw Material</th>
                          <th className="py-2.5 px-3 text-center">Qty / Recipe</th>
                          <th className="py-2.5 px-3 text-center">Unit Cost (Rs.)</th>
                          <th className="py-2.5 px-3 text-right">Line COGS</th>
                          <th className="py-2.5 px-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {recipeIngredients.map((ing) => {
                          const lineCOGS = ing.quantity * ing.unitCost
                          return (
                            <tr key={ing.id} className="hover:bg-orange-50/30 transition-colors">
                              <td className="py-3 px-3">
                                <div className="font-semibold text-gray-900">{ing.name}</div>
                                <div className="text-[10px] text-gray-500">Unit: {getUnitName(ing.unit)}</div>
                              </td>

                              <td className="py-3 px-3 text-center">
                                <div className="inline-flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    value={ing.quantity}
                                    onChange={(e) => handleUpdateIngredientQty(ing.id, e.target.value)}
                                    className="w-16 h-8 px-2 border border-gray-300 rounded-lg text-center font-bold text-gray-800 focus:ring-1 focus:ring-orange-500"
                                  />
                                  <span className="text-[10px] text-gray-500">{getUnitName(ing.unit)}</span>
                                </div>
                              </td>

                              <td className="py-3 px-3 text-center">
                                <div className="inline-flex items-center gap-1">
                                  <span className="text-gray-400">Rs.</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    value={ing.unitCost}
                                    onChange={(e) => handleUpdateIngredientCost(ing.id, e.target.value)}
                                    className="w-20 h-8 px-2 border border-gray-300 rounded-lg text-center font-bold text-gray-800 focus:ring-1 focus:ring-orange-500"
                                  />
                                </div>
                              </td>

                              <td className="py-3 px-3 text-right font-extrabold text-gray-900">
                                Rs.{lineCOGS.toFixed(2)}
                              </td>

                              <td className="py-3 px-3 text-center">
                                <button
                                  onClick={() => handleRemoveIngredient(ing.id)}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove from test recipe"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-orange-100/50 font-bold border-t-2 border-orange-200">
                          <td colSpan="3" className="py-3 px-3 text-right text-orange-900">
                            Total Recipe COGS per Item:
                          </td>
                          <td className="py-3 px-3 text-right text-base font-extrabold text-orange-950">
                            Rs.{recipeCOGSPerUnit.toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* Add Custom Raw Material Control */}
                <div className="pt-3 border-t border-gray-100 flex flex-col sm:flex-row items-center gap-3">
                  <select
                    value={selectedAddRmId}
                    onChange={(e) => setSelectedAddRmId(e.target.value)}
                    className="flex-1 h-9 px-3 border border-gray-300 rounded-xl text-xs font-semibold bg-white"
                  >
                    <option value="">-- Add Raw Material to Test Recipe --</option>
                    {rawMaterials.map((rm) => (
                      <option key={rm._id || rm.id} value={rm._id || rm.id}>
                        {rm.name} ({getUnitName(rm.unit)}) — GRN Price: Rs.{rm.cost || rm.purchasingPrice || 0}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={handleAddIngredient}
                    disabled={!selectedAddRmId}
                    className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-xl font-bold text-xs shadow-sm transition-all"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Ingredient</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Direct Product Cost Editor */
              <div className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <label className="block text-xs font-bold text-gray-700">Direct Product Buying / Purchasing Cost (Rs.)</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-500">Rs.</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={directProductCost}
                    onChange={(e) => setDirectProductCost(Math.max(0, Number(e.target.value) || 0))}
                    className="w-48 h-10 px-3 border border-gray-300 rounded-xl font-extrabold text-base text-gray-900 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-xs text-gray-500">per unit</span>
                </div>
              </div>
            )}
          </div>

          {/* Raw Material Inventory Forecast for Sold Qty */}
          {isRecipe && recipeIngredients.length > 0 && (
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-3">
              <h4 className="font-bold text-sm text-gray-900 flex items-center gap-2">
                <Package className="h-4 w-4 text-orange-600" />
                Raw Material Consumption Required for {soldQuantity} Units
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {recipeIngredients.map((ing) => {
                  const totalRequiredQty = ing.quantity * soldQuantity
                  const totalCostForRM = totalRequiredQty * ing.unitCost

                  return (
                    <div key={ing.id} className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                      <div className="font-bold text-gray-800 truncate">{ing.name}</div>
                      <div className="text-orange-600 font-extrabold text-sm mt-1">
                        {totalRequiredQty.toLocaleString(undefined, { maximumFractionDigits: 2 })} {getUnitName(ing.unit)}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        Total Purchase Cost: <span className="font-bold text-gray-700">Rs.{totalCostForRM.toFixed(2)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Financial Income, Profit & Loss Analysis */}
        <div className="lg:col-span-5 space-y-4">
          {/* Main Profitability Banner */}
          <div
            className={`p-6 rounded-2xl border shadow-md transition-all ${
              isProfitable
                ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300 text-emerald-950'
                : 'bg-gradient-to-br from-rose-50 to-red-50 border-rose-300 text-rose-950'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                  isProfitable ? 'bg-emerald-600 text-white shadow-sm' : 'bg-rose-600 text-white shadow-sm animate-pulse'
                }`}
              >
                {isProfitable ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                {isProfitable ? 'PROFITABLE ITEM' : 'UNPROFITABLE / LOSS'}
              </span>

              <span className="text-xs font-extrabold opacity-80">
                {marginPercent.toFixed(1)}% Margin
              </span>
            </div>

            <div className="mt-2">
              <div className="text-xs font-semibold opacity-75 uppercase tracking-wide">
                {isProfitable ? 'Net Profit Earned' : 'Net Financial Loss'} ({soldQuantity} units)
              </div>
              <div
                className={`text-3xl md:text-4xl font-black mt-1 ${
                  isProfitable ? 'text-emerald-700' : 'text-rose-700'
                }`}
              >
                {isProfitable ? '+' : ''}Rs.{netProfitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <p className="text-xs mt-3 opacity-90 leading-relaxed">
              {isProfitable ? (
                <>
                  🎉 This menu item yields <strong className="font-bold">Rs.{marginPerUnit.toFixed(2)}</strong> profit per unit sold. Selling {soldQuantity} units brings <strong className="font-bold">Rs.{netProfitLoss.toFixed(2)}</strong> actual net income.
                </>
              ) : (
                <>
                  ⚠️ Selling this item at Rs.{sellingPrice} loses <strong className="font-bold">Rs.{Math.abs(marginPerUnit).toFixed(2)}</strong> per unit because recipe COGS (Rs.{recipeCOGSPerUnit.toFixed(2)}) exceeds selling price!
                </>
              )}
            </p>
          </div>

          {/* Unit Economics Breakdown Card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h4 className="font-bold text-sm text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <Calculator className="h-4 w-4 text-orange-500" />
              Per-Unit Profitability Breakdown
            </h4>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-600">Menu Selling Price:</span>
                <span className="font-bold text-gray-900 text-sm">Rs.{Number(sellingPrice).toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center py-1 text-red-600">
                <span className="text-gray-600">- Recipe / Product COGS:</span>
                <span className="font-bold text-sm">- Rs.{recipeCOGSPerUnit.toFixed(2)}</span>
              </div>

              <div className="border-t border-dashed border-gray-200 pt-2 flex justify-between items-center font-extrabold text-sm">
                <span className="text-gray-800">Gross Margin per Unit:</span>
                <span className={marginPerUnit >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                  {marginPerUnit >= 0 ? '+' : ''}Rs.{marginPerUnit.toFixed(2)} ({marginPercent.toFixed(1)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Total Projection Summary Card */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h4 className="font-bold text-sm text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Total Period Performance ({soldQuantity} Units)
            </h4>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between items-center py-1.5 bg-emerald-50/50 px-3 rounded-lg">
                <span className="font-semibold text-emerald-900">Total Income / Revenue:</span>
                <span className="font-black text-sm text-emerald-700">Rs.{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center py-1.5 bg-rose-50/50 px-3 rounded-lg">
                <span className="font-semibold text-rose-900">Total Purchasing / Recipe Cost:</span>
                <span className="font-black text-sm text-rose-700">Rs.{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center py-2 px-3 bg-gray-900 text-white rounded-xl font-black text-sm">
                <span>Net Earnings:</span>
                <span className={isProfitable ? 'text-emerald-400' : 'text-rose-400'}>
                  {isProfitable ? '+' : ''}Rs.{netProfitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecipeCalculatorTab
